import { useTranslation } from "react-i18next"

import { formatXpNumber } from "@/lib/format-xp"
import { cn } from "@/lib/utils"

/**
 * OKS-48 — Per-row XP chip on the Brand crew roster (UX Spec §2.3).
 *
 * Inserted under the existing engagement-rate row in `<CrewListRow>`.
 * Reads `member.xp_balance` (BE serialiser key — see
 * `CrewMembershipsController#serialise_row`).
 *
 * Format: `XP: 1,247` (locale-aware grouping). Empty-state renders
 * `XP: 0` (UX Spec §3 — never `—`, balance defaults to 0 on an empty
 * ledger).
 *
 * Truncates with ellipsis on narrow cards (UX Spec §5 — "horizontal
 * truncation if card is narrow"); the `aria-label` carries the full
 * number for screen readers.
 *
 * # Locked testid
 *
 *   - `crew-list-xp-chip` — chip wrapper, indexed by parent row
 *     `data-member-id`. QA matrix asserts the chip renders on every
 *     active membership row + the value matches the BE wire key.
 */
interface CrewListXpChipProps {
  balance: number
  className?: string
}

export function CrewListXpChip({ balance, className }: CrewListXpChipProps) {
  const { t, i18n } = useTranslation()
  const formatted = formatXpNumber(balance, i18n.language)
  const text = t("xp.chip.label", { count: formatted })

  return (
    <span
      data-testid="crew-list-xp-chip"
      data-xp-balance={balance}
      aria-label={text}
      className={cn(
        "text-muted-foreground inline-flex truncate text-xs tabular-nums",
        className,
      )}
    >
      {text}
    </span>
  )
}
